import React, { FC, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { Translate } from "@/app/application/Translate";
import { NewCourseSession } from "@/features/training/pages/training-tab/panel-right/training-course/NewCourseSession";
import { CustomActionsButton } from "@/features/training/ui";
import { EditTrainingCourse } from "@/features/training/ui";
import { StyledMenu } from "@/shared/ui/StyledMenu";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import InventoryIcon from "@mui/icons-material/Inventory";
import SchoolIcon from "@mui/icons-material/School";
import {
	Box,
	Divider,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Tooltip,
} from "@mui/material";

interface TrainingCourseActionsProps {
	courseId: number;
}

export const TrainingCourseActions: FC<TrainingCourseActionsProps> = ({
	courseId,
}) => {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const showActiveSessions = pathname.includes("archive");

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [editCourseModal, setEditCourseModal] = useState(false);
	const [newSessionModal, setNewSessionModal] = useState(false);

	const open = Boolean(anchorEl);
	const handleClick = (e: React.MouseEvent<HTMLElement>) =>
		setAnchorEl(e.currentTarget);
	const handleClose = () => setAnchorEl(null);

	const openEdit = () => {
		setEditCourseModal(true);
		handleClose();
	};
	const openNewSession = () => {
		setNewSessionModal(true);
		handleClose();
	};
	const toggleArchiveView = () => {
		const path = showActiveSessions
			? `/training/courses/${courseId}`
			: `/training/courses/${courseId}/archive`;
		navigate(path);
		handleClose();
	};

	// Compact menu styling (same as the other menus)
	const paperSx = useMemo(
		() => ({
			borderRadius: 3,
			mt: 1,
			minWidth: 240,
			overflow: "hidden",
			border: "1px solid",
			borderColor: "divider",
			boxShadow:
				"0px 14px 30px rgba(0,0,0,0.12), 0px 6px 12px rgba(0,0,0,0.08)",
		}),
		[],
	);

	const itemSx = useMemo(
		() => ({
			mx: 0.75,
			my: 0.25,
			borderRadius: 2,
			px: 1,
			py: 0.75,
			minHeight: 38,
			"& .MuiListItemIcon-root": {
				minWidth: 30,
				color: "text.primary", // monochrome like screenshot
			},
			"& .MuiListItemText-primary": {
				fontSize: 14.5,
				fontWeight: 500,
				letterSpacing: 0.1,
			},
			"&:hover": { backgroundColor: "action.hover" },
		}),
		[],
	);

	return (
		<Box>
			<Tooltip
				title={
					<Translate String="Training.Course.Actions.Tooltip">
						Edit the course, schedule a new session, or view archived sessions
					</Translate>
				}
				enterDelay={600}
			>
				<Box display="inline-flex">
					<CustomActionsButton
						open={open}
						handleClick={handleClick}
						aria-haspopup="menu"
						aria-controls={open ? "training-course-actions-menu" : undefined}
						aria-expanded={open ? "true" : undefined}
					>
						<Translate String="Training.Course.Actions">Actions</Translate>
					</CustomActionsButton>
				</Box>
			</Tooltip>

			<StyledMenu
				id="training-course-actions-menu"
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				keepMounted
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
				PaperProps={{ sx: paperSx }}
				MenuListProps={{
					"aria-labelledby": "training-course-actions-button",
					dense: false,
					sx: { py: 0.5 },
				}}
			>
				<MenuItem sx={itemSx} onClick={openEdit}>
					<ListItemIcon>
						<SchoolIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={
							<Translate
								HTMLTag="span"
								String="Training.Course.Actions.EditCourse"
							>
								Edit course
							</Translate>
						}
					/>
				</MenuItem>

				<MenuItem sx={itemSx} onClick={openNewSession}>
					<ListItemIcon>
						<HistoryEduIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={
							<Translate
								HTMLTag="span"
								String="Training.Course.Actions.NewTrainingSession"
							>
								New training session
							</Translate>
						}
					/>
				</MenuItem>

				<Divider sx={{ my: 0.5, mx: 1.5 }} />

				<MenuItem sx={itemSx} onClick={toggleArchiveView}>
					<ListItemIcon>
						<InventoryIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={
							showActiveSessions ? (
								<Translate
									HTMLTag="span"
									String="Training.Course.Actions.ActiveSessions"
								>
									Active sessions
								</Translate>
							) : (
								<Translate
									HTMLTag="span"
									String="Training.Course.Actions.ArchivedSessions"
								>
									Archived sessions
								</Translate>
							)
						}
					/>
				</MenuItem>
			</StyledMenu>

			{/* Modals */}
			<EditTrainingCourse
				courseId={courseId}
				open={editCourseModal}
				handleClose={() => setEditCourseModal(false)}
			/>
			<NewCourseSession
				courseId={courseId}
				open={newSessionModal}
				handleClose={() => setNewSessionModal(false)}
			/>
		</Box>
	);
};
